import React from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  Link as MuiLink,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";
import { truncate, getFirstPathParam } from "bundles/common/utils/utils";
import HTML from "bundles/common/components/HTML";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  link: {
    display: "flex",
    height: "100%",
  },
  card: {
    cursor: "pointer",
    overflow: "hidden",
    borderRadius: "8px !important",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    flex: 1,
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    },
    "&:hover $media": {
      transform: "scale(1.05)",
    },
  },
  mediaWrapper: {
    position: "relative",
    paddingTop: "62.5%",
    overflow: "hidden",
  },
  media: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.5s ease",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    gap: 8,
    padding: 16,
  },
  date: {
    fontSize: "0.75rem !important",
    color: "hsl(220 10% 46%)",
  },
  title: {
    fontSize: 16,
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif !important",
    lineHeight: 1.375,
    color: "hsl(220 40% 13%)",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  excerpt: {
    fontSize: "0.875rem !important",
    lineHeight: 1.25,
    color: "hsl(220 10% 46%)",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  authorRow: {
    marginTop: "auto",
    fontSize: "0.75rem !important",
    color: "hsl(220 10% 46%)",
  },
  authorName: {
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif !important",
    color: "hsl(152 60% 44%)",
  },
}));

const ArticleCard = ({ post, companyId }) => {
  const classes = useStyles();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <MuiLink
      href={`/${i18nLocale}/${getFirstPathParam()}/posts/${post.encoded_title}/${post.id}`}
      underline="none"
      className={classes.link}
    >
      <Card className={classes.card} elevation={0}>
        <Box className={classes.mediaWrapper}>
          <img
            className={classes.media}
            src={post.cover_picture_url}
            alt={post.title}
            loading="lazy"
          />
        </Box>
        <CardContent className={classes.content}>
          <Typography className={classes.date}>
            {moment(post.created_at).format("DD/MM/YYYY")}
          </Typography>
          <Typography className={classes.title}>
            {truncate(post.title, 60)}
          </Typography>
          <Typography className={classes.excerpt} component="div">
            <HTML
              html={truncate(post.content, 100)}
              blacklistedTags={["strong", "p"]}
            />
          </Typography>
          <Typography className={classes.authorRow}>
            <FormattedMessage id="landing_candidate.from" />{" "}
            {companyId ? (
              <MuiLink
                href={`/companies/squarehub/${companyId}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  "&:hover": { color: "#035d2a" },
                }}
              >
                <span className={classes.authorName}>Squarehub</span>
              </MuiLink>
            ) : (
              <span className={classes.authorName}>Squarehub</span>
            )}
          </Typography>
        </CardContent>
      </Card>
    </MuiLink>
  );
};

export default ArticleCard;
